<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="annotation-details-container" ref="playground">
    <annotation-details v-if="selectedFeature?.properties?.annot?.hasOwnProperty('user')" 
      :index="index"
      :annotation="annot" 
      :terms="terms" 
      :images="images" 
      :slices="slices" 
      :profiles="profiles" 
      :tracks="tracks"
      :users="allUsers" 
      :showImageInfo="false" 
      :showChannelInfo="showChannelInfo" 
      :key="selectedFeature.id"
      :showComments="showComments" 
      @addTerm="$emit('addTerm', $event)" 
      @addTrack="$emit('addTrack', $event)"
      @updateTerms="$emit('updateTermsOrTracks', annot)" 
      @updateTracks="$emit('updateTermsOrTracks', annot)"
      @updateProperties="$emit('updateProperties')"
       @select="$emit('select', $event)"
      @centerView="$emit('centerView', ($event) ? $event : annot)"
       @deletion="$emit('delete', annot)" />
  </div>
</template>

<script>
import VueDraggableResizable from 'vue-draggable-resizable';
import { UserCollection } from '@/api';
import AnnotationDetails from '@/components/annotations/AnnotationDetails';
import AnnotationLinksPreview from '@/components/annotations/AnnotationLinksPreview';
import AnnotationSimpleDetails from '@/components/viewer/annotations/AnnotationSimpleDetails';

export default {
  name: 'annotation-details-container',
  components: {
    AnnotationDetails,
    AnnotationLinksPreview,
    AnnotationSimpleDetails,
    VueDraggableResizable,
  },
  props: {
    index: String,
  },
  data() {
    return {
      projectUsers: [],
      showComments: false
    };
  },
  computed: {
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    images() {
      if (this.imageWrapper.imageGroup) {
        return [this.image, ...this.imageWrapper.imageGroup.imageInstances];
      }
      return [this.image];
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    showChannelInfo() {
      return this.imageWrapper.activeSlices && this.imageWrapper.activeSlices.length > 1;
    },
    profiles() {
      return this.imageWrapper.profile ? [this.imageWrapper.profile] : [];
    },
    selectedFeature() {
      return this.$store.getters[this.imageModule + 'selectedFeature'];
    },
    allUsers() {
      return this.projectUsers;
    },
    annot() {
      return this.selectedFeature ? this.selectedFeature?.properties?.annot : {};
    },
    terms() {
      return this.$store.getters[this.imageModule + 'terms'] || [];
    },
    tracks() {
      return this.imageWrapper.tracks.tracks;
    }
  },
  watch: {
    selectedFeature() {
      if (this.selectedFeature) {
        // this.displayAnnotDetails = true;
        let targetAnnot = this.imageWrapper.selectedFeatures.showComments;
        this.showComments = (targetAnnot === this.annot.id);
        if (targetAnnot !== null) {
          this.$store.commit(this.imageModule + 'setShowComments', null);
        }
      }
    }
  },
  methods: {
    async fetchUsers() {
      let collection = new UserCollection({
        filterKey: 'project',
        filterValue: this.image.project.id,
      });

      this.projectUsers = (await collection.fetchAll()).array;
    },
  },
  created() {
    this.fetchUsers();
  },
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.draggable {
  background: $dark-bg-secondary;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.5), 0 0 0 1px rgba(10, 10, 10, 0.5);
  pointer-events: auto;
  color: $dark-text-primary;
}

.actions {
  padding: 0.35em;
  text-align: right;
  background-color: $dark-bg-panel;
  border-bottom: 1px solid $dark-border-color;
  border-radius: 5px 5px 0 0;
  display: flex;
  align-items: center;
}

h1 {
  font-size: 0.9rem;
  padding: 0;
  flex: 1;
  text-align: left;
  margin-left: 0.4em;
  color: $dark-text-primary;
}

.actions .button {
  margin-left: 0.25rem;
  width: 1.75rem;
  background-color: $dark-button-bg;
  color: $dark-text-primary;
  border: 1px solid $dark-button-border;
}

.actions .button:hover {
  background-color: $dark-button-hover-bg;
}

.annotation-details-container {
  padding: 0em;
  overflow-y: auto;
  height: 100%;
  background-color: $dark-bg-primary;
  color: $dark-text-primary;
}

/* 深色模式滚动条样式 */
.annotation-details-container::-webkit-scrollbar,
.draggable::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.annotation-details-container::-webkit-scrollbar-track,
.draggable::-webkit-scrollbar-track {
  background: $dark-scrollbar-track;
}

.annotation-details-container::-webkit-scrollbar-thumb,
.draggable::-webkit-scrollbar-thumb {
  background: $dark-scrollbar-thumb;
  border-radius: 4px;
}

.annotation-details-container::-webkit-scrollbar-thumb:hover,
.draggable::-webkit-scrollbar-thumb:hover {
  background: $dark-scrollbar-thumb-hover;
}
</style>

<style lang="scss">
@import '../../assets/styles/dark-variables';

.dragging .button.drag {
  background-color: #6899d0;
  color: #fff;
}

.annotation-details-playground .draggable {
  z-index: 15 !important;
}

/* 深色模式下覆盖全局样式 */
.annotation-details-playground :deep(.table) {
  background-color: $dark-table-bg;
  color: $dark-text-primary;
}

.annotation-details-playground :deep(.table td),
.annotation-details-playground :deep(.table th) {
  border-color: $dark-table-border;
}

.annotation-details-playground :deep(.table tr:hover) {
  background-color: $dark-table-hover-bg;
}

.annotation-details-playground :deep(.tag) {
  background-color: $dark-tag-bg;
  color: $dark-text-primary;
  border: 1px solid $dark-tag-border;
}

.annotation-details-playground :deep(.tag:hover) {
  background-color: $dark-tag-hover-bg;
}

.annotation-details-playground :deep(.button) {
  background-color: $dark-button-bg;
  color: $dark-text-primary;
  border: 1px solid $dark-button-border;
}

.annotation-details-playground :deep(.button:hover) {
  background-color: $dark-button-hover-bg;
  border-color: $dark-button-hover-border;
}

.annotation-details-playground :deep(.input),
.annotation-details-playground :deep(.textarea),
.annotation-details-playground :deep(.select select) {
  background-color: $dark-input-bg;
  color: $dark-text-primary;
  border-color: $dark-input-border;
}

.annotation-details-playground :deep(.input::placeholder),
.annotation-details-playground :deep(.textarea::placeholder),
.annotation-details-playground :deep(.select select::placeholder) {
  color: $dark-text-disabled;
}

.annotation-details-playground :deep(.input:focus),
.annotation-details-playground :deep(.textarea:focus),
.annotation-details-playground :deep(.select select:focus) {
  border-color: $dark-input-focus-border;
  box-shadow: 0 0 0 0.2rem $dark-input-focus-shadow;
}
</style>
